import React from "react";
import { act, render, screen, fireEvent } from "@testing-library/react";
import "@testing-library/jest-dom";
import "@testing-library/jest-dom";

import { FullPageChatLayout } from "./FullPageChatLayout";

jest.mock("@metalmind-ab/mmagent-chat-ui", () => ({
  ChatMessage: () => null,
  JobProgressIndicator: () => null,
  useChatStore: (selector: (state: any) => any) =>
    selector({
      backgroundJobs: [],
      removeBackgroundJob: jest.fn(),
      messages: [],
    }),
}));

beforeAll(() => {
  Object.defineProperty(HTMLElement.prototype, "scrollIntoView", {
    configurable: true,
    value: jest.fn(),
  });
  Object.defineProperty(URL, "createObjectURL", {
    configurable: true,
    value: jest.fn(() => "blob:test-url"),
  });
  Object.defineProperty(URL, "revokeObjectURL", {
    configurable: true,
    value: jest.fn(),
  });
});

const SUGGESTIONS = ["How does Q3 compare to Q2?", "What drove the growth?"];

const renderLayout = (
  overrides: Partial<React.ComponentProps<typeof FullPageChatLayout>> = {},
) =>
  render(
    <FullPageChatLayout
      messages={[]}
      onSendMessage={jest.fn()}
      isLoading={false}
      conversations={[]}
      onLoadConversation={jest.fn()}
      onNewConversation={jest.fn()}
      onSearchConversations={jest.fn()}
      onLoadMoreConversations={jest.fn()}
      conversationsLoading={false}
      currentConversationId={1}
      welcomeTitle="Welcome to WS"
      welcomeDescription="Start a new conversation"
      {...overrides}
    />,
  );

describe("FullPageChatLayout follow-up suggestions", () => {
  it("sends the clicked suggestion as the next message", () => {
    const onSendMessage = jest.fn();
    renderLayout({ followUpSuggestions: SUGGESTIONS, onSendMessage });

    fireEvent.click(
      screen.getByRole("button", { name: "What drove the growth?" }),
    );

    expect(onSendMessage).toHaveBeenCalledWith("What drove the growth?");
  });

  it("captures a plain-text draft with a restore callback before sending", () => {
    const onTextSendStarted = jest.fn();
    renderLayout({ onTextSendStarted });
    const composer = screen.getByRole("textbox");
    fireEvent.change(composer, { target: { value: "draft to restore" } });

    fireEvent.click(screen.getByRole("button", { name: "Send message" }));

    expect(onTextSendStarted).toHaveBeenCalledWith(
      "draft to restore",
      expect.any(Function),
    );
    act(() => onTextSendStarted.mock.calls[0][1]());
    expect(composer).toHaveValue("draft to restore");
  });

  it("keeps a newer composer draft when a pending typed send fails", async () => {
    let rejectSend: (error: Error) => void = () => {
      throw new Error("send was not started");
    };
    const onSendMessage = jest.fn(
      () =>
        new Promise<void>((_resolve, reject) => {
          rejectSend = reject;
        }),
    );
    const onTextSendStarted = jest.fn();
    renderLayout({ onSendMessage, onTextSendStarted });
    const composer = screen.getByRole("textbox");

    fireEvent.change(composer, { target: { value: "original draft" } });
    fireEvent.click(screen.getByRole("button", { name: "Send message" }));
    fireEvent.change(composer, { target: { value: "newer draft" } });

    await act(async () => {
      rejectSend(new Error("request failed"));
      await Promise.resolve();
    });

    expect(composer).toHaveValue("newer draft");
    const restoreDraft = onTextSendStarted.mock.calls[0][1] as () => void;
    act(() => restoreDraft());
    expect(composer).toHaveValue("newer draft");
  });

  it("renders no chips when there are no suggestions", () => {
    renderLayout();

    expect(
      screen.queryByTestId("follow-up-suggestions"),
    ).not.toBeInTheDocument();
  });

  it.each([
    ["chat is disabled", { chatDisabled: true }],
    ["a conversation is switching", { conversationSwitching: true }],
    ["an agent is being created", { agentCreationPending: true }],
    ["files are uploading", { isUploading: true }],
  ])("hides chips while %s", (_label, blockingProps) => {
    renderLayout({ followUpSuggestions: SUGGESTIONS, ...blockingProps });

    expect(
      screen.queryByTestId("follow-up-suggestions"),
    ).not.toBeInTheDocument();
  });

  it("keeps chips visible while streaming and queues the selected suggestion", () => {
    const onQueueMessage = jest.fn().mockResolvedValue(undefined);
    const onSendMessage = jest.fn();
    renderLayout({
      followUpSuggestions: SUGGESTIONS,
      isLoading: true,
      onQueueMessage,
      onSendMessage,
    });

    fireEvent.click(
      screen.getByRole("button", { name: "What drove the growth?" }),
    );

    expect(onQueueMessage).toHaveBeenCalledWith("What drove the growth?", []);
    expect(onSendMessage).not.toHaveBeenCalled();
  });
});
